import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Camera, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import { updateCameraModal } from '@/store/slices/appStateSlice';
import { linkPhoto, queuePhotoUpload } from '@/store/actions/photosActions';
import { FileCache } from '@/lib/cache-storage';
import type { RootState } from '@/store';

interface CameraModalProps {
  className?: string;
}

const CameraModal: React.FC<CameraModalProps> = ({ className }) => {
  const dispatch = useDispatch();
  const cameraModal = useSelector((state: RootState) => state.appState.cameraModal);
  const task = useSelector((state: RootState) =>
    state.tasks.items.find(task => task.id === cameraModal.taskID)
  );
  const photos = useSelector((state: RootState) =>
    state.photos.items.filter(photo =>
      task && photo.task_id === task.id && photo.tag === cameraModal.tag
    )
  );

  const [view, setView] = useState<'camera' | 'preview' | 'gallery'>('camera');
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check available cameras
  const checkAvailableCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setAvailableCameras(videoDevices);
      setHasMultipleCameras(videoDevices.length > 1);
      console.log('📹 Available cameras:', videoDevices.length);
    } catch (error) {
      console.error('Error enumerating devices:', error);
      setHasMultipleCameras(false);
    }
  }, []);

  // Start camera stream
  const startCamera = useCallback(async () => {
    console.log("startCamera");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Unable to access camera. Please check permissions.');
    }
  }, []); // Remove facingMode dependency

  // Stop camera stream
  const stopCamera = useCallback(() => {
    setCameraStream(prevStream => {
      if (prevStream) {
        prevStream.getTracks().forEach(track => track.stop());
      }
      return null;
    });
  }, []);

  // Handle modal open/close
  useEffect(() => {
    if (cameraModal.show) {
      checkAvailableCameras();
      if (view === 'camera') {
        startCamera();
      }
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [cameraModal.show, view, facingMode, checkAvailableCameras, startCamera, stopCamera]);

  const handleClose = () => {
    stopCamera();
    setView('camera');
    setCapturedPhoto(null);
    setIsProcessing(false);
    dispatch(updateCameraModal({ show: false, tag: false, taskID: false }));
  };

  const handleTakePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to data URL
    const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedPhoto(photoDataUrl);
    setView('preview');
    stopCamera();
  }, [stopCamera]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setCapturedPhoto(result);
      setView('preview');
      stopCamera();
    };
    reader.readAsDataURL(file);
  };

  const handleRetake = () => {
    setCapturedPhoto(null);
    setView('camera');
    startCamera();
  };

  const handleSavePhoto = async (closeAfter = false) => {
    if (!capturedPhoto || !task) return;

    setIsProcessing(true);

    try {
      // Create a unique filename and temp_id
      const filename = `photo_${task.job_id}_${Date.now()}.jpg`;
      const temp_id = Math.random().toString(36).substr(2, 8);

      // Convert data URL to File object
      const file = await FileCache.dataURLtoFile(capturedPhoto, filename);

      // Store file in cache storage for offline access
      let cachedSuccessfully = false;
      if (FileCache.isAvailable()) {
        try {
          await FileCache.storeFile(file, temp_id);
          cachedSuccessfully = true;
          console.log('✅ Photo cached successfully for offline access');
        } catch (cacheError) {
          console.warn('⚠️ Failed to cache photo, but will still save to Redux:', cacheError);
          // Continue even if caching fails - photo will still work online
        }
      }

      // Don't create blob URL - let PhotoThumbnail component handle cache retrieval

      // 1. Add photo metadata to Redux (no File objects or blob URLs - serializable only)
      dispatch(linkPhoto({
        local: filename,
        asset_id: temp_id, // Use temp_id as asset_id for web
        uri: cachedSuccessfully ? undefined : undefined, // No URI - will load from cache by temp_id
        originalLocal: null,
        original_asset_id: null,
        originalUri: null,
        tag: cameraModal.tag,
        task_id: task.id,
        job_id: task.job_id,
        temp_id: temp_id,
        // Offline storage metadata (serializable)
        cached: cachedSuccessfully,
        fileSize: file.size,
        fileType: file.type,
        storedAt: new Date().toISOString(),
        // Cache location reference for PhotoThumbnail component
        cacheKey: temp_id // This tells PhotoThumbnail where to find the cached file
      }));

      // 2. Queue upload for when online (using Redux Offline)
      if (cachedSuccessfully) {
        try {
          dispatch(queuePhotoUpload({
            temp_id: temp_id,
            local: filename,
            tag: cameraModal.tag,
            task_id: task.id,
            job_id: task.job_id,
            asset_id: temp_id,
            fileType: file.type,
            storedAt: new Date().toISOString()
          }));
          console.log('✅ Photo upload queued for when online');
        } catch (uploadError) {
          console.error('❌ Error queueing photo upload:', uploadError);
        }
      }

      toast.success(cachedSuccessfully ?
        'Photo saved and cached for offline access!' :
        'Photo saved successfully!'
      );

      if (closeAfter) {
        handleClose();
      } else {
        setCapturedPhoto(null);
        setView('camera');
        startCamera();
      }
    } catch (error) {
      console.error('Error saving photo:', error);
      toast.error('Failed to save photo. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  if (!task) {
    return null;
  }

  return (
    <Sheet open={cameraModal.show} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="bottom"
        className={cn(
          'h-[95vh] rounded-t-lg flex flex-col',
          className
        )}
      >
        <SheetHeader className="flex-none">
          <SheetTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Take Photo
            {cameraModal.tag && (
              <Badge variant="secondary">
                {String(cameraModal.tag)}
              </Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">

          {view === 'camera' && (
              <>
              <div className="flex-1 rounded-lg overflow-hidden relative min-h-0">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-contain"
                />
                <canvas
                  ref={canvasRef}
                  className="hidden"
                />
              </div>
              </>
          )}

          {view === 'preview' && capturedPhoto && (
            <>
              <div className="flex-1 bg-gray-100 rounded-lg overflow-hidden min-h-0">
                <img
                  src={capturedPhoto}
                  alt="Captured photo"
                  className="w-full h-full object-contain"
                />
              </div>
            </>
          )}

          {view === 'gallery' && (
            <div className="flex-1 overflow-auto p-4 min-h-0">
              {photos.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <Camera className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No photos yet</p>
                    <p className="text-sm">Take some photos to see them here</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 pb-20">
                  {photos.map((photo) => (
                    <PhotoThumbnail key={photo.temp_id} photo={photo} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sticky bottom buttons */}
        <div className="flex-none bg-white border-t">
          {view === 'camera' && (
            <div className="flex items-center justify-between p-4">
              <Button
                variant="outline"
                onClick={handleClose}
              >
                Cancel
              </Button>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Computer
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setView('gallery')}
                >
                  Gallery ({photos.length})
                </Button>
                {hasMultipleCameras && (
                  <Button
                    variant="outline"
                    onClick={toggleCamera}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  onClick={handleTakePhoto}
                  disabled={!cameraStream}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Take Photo
                </Button>
              </div>
            </div>
          )}

          {view === 'preview' && (
            <div className="flex items-center justify-between p-4">
              <Button
                variant="outline"
                onClick={handleRetake}
                disabled={isProcessing}
              >
                Retake
              </Button>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleSavePhoto(false)}
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Saving...' : 'Save & Take Another'}
                </Button>
                <Button
                  onClick={() => handleSavePhoto(true)}
                  disabled={isProcessing}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isProcessing ? 'Saving...' : 'Save & Close'}
                </Button>
              </div>
            </div>
          )}

          {view === 'gallery' && (
            <div className="flex items-center justify-between p-4">
              <Button
                variant="outline"
                onClick={() => setView('camera')}
              >
                Back to Camera
              </Button>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Add from Computer
                </Button>
                <Button
                  onClick={handleClose}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Hidden file input for computer uploads */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </SheetContent>
    </Sheet>
  );
};

// Component to handle photo thumbnails with cache loading
const PhotoThumbnail: React.FC<{ photo: any }> = ({ photo }) => {
  const [imageSrc, setImageSrc] = useState<string | null>(photo.uri || null);
  const [isLoading, setIsLoading] = useState(!photo.uri);
  const [error, setError] = useState(false);

  console.log('🖼️ PhotoThumbnail:', {
    temp_id: photo.temp_id,
    asset_id: photo.asset_id,
    uri: photo.uri,
    cached: photo.cached,
    status: photo.status,
    filename: photo.filename,
    job_id: photo.job_id,
    imageSrc: imageSrc,
    isLoading: isLoading,
    error: error
  });

  useEffect(() => {
    console.log('🔄 Loading photo:', photo.temp_id, 'asset_id:', photo.asset_id);

    const loadPhoto = async () => {
      try {
        setIsLoading(true);
        setError(false);

        // 1. First try existing URI (newly taken photos)
        if (photo.uri) {
          console.log('✅ Using existing URI:', photo.uri);
          setImageSrc(photo.uri);
          setIsLoading(false);
          return;
        }

        // 2. Try cache using temp_id (if photo is still cached)
        if (photo.cached && photo.temp_id) {
          console.log('📥 Photo is cached, loading from cache:', photo.temp_id);
          try {
            const cachedBlob = await FileCache.retrieveFileBlob(photo.temp_id);

            if (cachedBlob && cachedBlob.size > 0) {
              console.log('✅ Found in cache:', photo.temp_id, 'Size:', cachedBlob.size);
              const objectUrl = URL.createObjectURL(cachedBlob);
              console.log('🔗 Created fresh object URL for cached photo:', objectUrl);
              setImageSrc(objectUrl);
              setIsLoading(false);
              return;
            } else {
              console.log('❌ Cached blob is empty or invalid for:', photo.temp_id);
            }
          } catch (cacheError) {
            console.error('❌ Cache retrieval error for temp_id:', photo.temp_id, cacheError);
          }
        }

        // 3. For uploaded photos (cached=false), load from server
        if (photo.filename && photo.job_id) {
          const baseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || '';
          const remoteUrl = `${baseUrl}/any-image/${photo.job_id}/${photo.filename}`;
          console.log('🌐 Trying remote URL:', remoteUrl);
          setImageSrc(remoteUrl);
          setIsLoading(false);
          return;
        }

        console.log('❌ No loading options available for:', photo.temp_id);
        setError(true);
        setIsLoading(false);

      } catch (err) {
        console.error('❌ Photo loading failed:', photo.temp_id, err);
        setError(true);
        setIsLoading(false);
      }
    };

    loadPhoto();
  }, [photo.uri, photo.temp_id, photo.asset_id, photo.filename, photo.job_id]);

  // Cleanup object URL when component unmounts
  useEffect(() => {
    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageSrc]);

  return (
    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative group">
      {isLoading ? (
        <div className="w-full h-full flex items-center justify-center text-gray-400">
          <Camera className="h-8 w-8" />
        </div>
      ) : error || !imageSrc ? (
        <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-200">
          <div className="text-center">
            <Camera className="h-8 w-8 mx-auto mb-1" />
            <p className="text-xs">Error loading</p>
          </div>
        </div>
      ) : (
        <img
          src={imageSrc}
          alt={`Photo ${photo.local}`}
          className="w-full h-full object-contain"
          onError={(e) => {
            console.error('❌ Image failed to load:', imageSrc, e);
            // If blob URL failed, try to reload from cache
            if (imageSrc && imageSrc.startsWith('blob:')) {
              console.log('🔄 Blob URL failed, attempting to reload from cache');
              // Trigger a reload by clearing the current src
              setImageSrc(null);
              // The useEffect will trigger again and try the fallback options
            } else {
              setError(true);
            }
          }}
        />
      )}


      {photo.status && (
        <div className="absolute top-2 right-2">
          {photo.status === 'pending' && (
            <div className="bg-yellow-500 text-white text-xs px-2 py-1 rounded">
              Pending
            </div>
          )}
          {photo.status === 'uploading' && (
            <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded">
              Uploading {photo.progress}%
            </div>
          )}
          {photo.status === 'completed' && (
            <div className="bg-green-500 text-white text-xs px-2 py-1 rounded">
              ✓
            </div>
          )}
          {photo.status === 'error' && (
            <div className="bg-red-500 text-white text-xs px-2 py-1 rounded">
              Error
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CameraModal;